<template>
  <b-modal
    size="md"
    centered
    lazy
    :visible="showModal"
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
    modal-class="system-restriction-modal"
  >
    <button class="close-button" @click="goBack" aria-label="Close">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <!-- User has access to the record's system -->
    <div v-if="hasAccess" class="restriction-content">
      <h5 class="restriction-title">{{ $t("system_restriction.title") }}</h5>
      <p class="restriction-text">{{ $t("system_restriction.description") }}</p>

      <div class="systems-row">
        <div class="system-card">
          <span class="system-label">{{ $t("system_restriction.current_system") }}</span>
          <img v-if="currentSystemInfo.logo" class="system-logo" :src="currentSystemInfo.logo" :alt="currentSystemInfo.name" />
          <span class="system-name">{{ currentSystemInfo.name }}</span>
        </div>

        <svg class="arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>

        <div class="system-card system-card-target">
          <span class="system-label">{{ $t("system_restriction.record_system") }}</span>
          <img v-if="recordSystemInfo.logo" class="system-logo" :src="recordSystemInfo.logo" :alt="recordSystemInfo.name" />
          <span class="system-name">{{ recordSystemInfo.name }}</span>
        </div>
      </div>

      <div class="actions">
        <b-button variant="outline-secondary" @click="goBack">
          {{ $t("system_restriction.go_back") }}
        </b-button>
        <b-button variant="primary" @click="switchSystem">
          {{ $t("system_restriction.switch_to", { system: recordSystemInfo.name }) }}
        </b-button>
      </div>
    </div>

    <!-- User has no access to the record's system -->
    <div v-else class="restriction-content">
      <svg class="sad-face" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M16 16s-1.5-2-4-2-4 2-4 2"></path>
        <line x1="9" y1="9" x2="9.01" y2="9"></line>
        <line x1="15" y1="9" x2="15.01" y2="9"></line>
      </svg>
      <h5 class="restriction-title">{{ $t("system_restriction.no_access_title") }}</h5>
      <p class="restriction-text">
        {{ $t("system_restriction.no_access_description", { system: recordSystemInfo.name }) }}
      </p>

      <div class="actions">
        <b-button variant="primary" @click="goBack">
          {{ $t("system_restriction.go_back") }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

// key: value stored in user.system (case matters, see Navbar.vue)
const SYSTEMS = {
  ulu: { key: "ULU", name: "ULU", logo: require("@/assets/ULU_logo.png") },
  ulubv: { key: "ulubv", name: "ULU b.v.", logo: require("@/assets/ulubv_logo.png") },
  uluuk: { key: "uluuk", name: "ULU Technologies UK", logo: require("@/assets/uluuk_logo.png") },
  fleetify: { key: "Fleetify", name: "Fleetify", logo: require("@/assets/fleetify_logo.png") },
  cartracker: { key: "Cartracker", name: "Cartracker", logo: require("@/assets/cartracker_logo.png") },
  traksi: { key: "traksi", name: "Traksi", logo: require("@/assets/traksi_logo.png") },
  requre: { key: "requre", name: "Requre", logo: require("@/assets/requre_logo.png") },
  finnik: { key: "finnik", name: "Finnik", logo: require("@/assets/finnik_logo.png") },
  national_netherlands: { key: "national_netherlands", name: "National Netherlands", logo: require("@/assets/ohra_logo.png") },
  imoto: { key: "imoto", name: "iMoto", logo: require("@/assets/imoto_logo.png") }
};

// Mounted once in Home.vue; opened by the systemRestriction mixin through the store
export default {
  computed: {
    ...mapGetters(["getUserSystem", "getUserPermissions", "getSystemRestrictionBrand"]),
    recordBrand() {
      return this.getSystemRestrictionBrand
        ? this.getSystemRestrictionBrand.toLowerCase()
        : null;
    },
    currentBrand() {
      return this.getUserSystem ? this.getUserSystem.toLowerCase() : null;
    },
    showModal() {
      return !!this.recordBrand && this.recordBrand !== this.currentBrand;
    },
    hasAccess() {
      return (this.getUserPermissions || []).some(
        permission => permission.brand === this.recordBrand
      );
    },
    currentSystemInfo() {
      return this.systemInfo(this.currentBrand);
    },
    recordSystemInfo() {
      return this.systemInfo(this.recordBrand);
    }
  },
  methods: {
    ...mapMutations(["setSystem", "setSystemRestrictionBrand"]),
    systemInfo(brand) {
      return SYSTEMS[brand] || { key: brand, name: brand || "-", logo: null };
    },
    switchSystem() {
      // The page reloads itself through the mixin's getUserSystem watcher
      this.setSystem(this.recordSystemInfo.key);
    },
    goBack() {
      this.setSystemRestrictionBrand(null);
      // Records are often opened in a new tab, so there may be no history to go back to
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "dashboard" });
      }
    }
  }
};
</script>

<style scoped>
/* Close Button */
.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
  width: 36px;
  height: 36px;
}

.close-button:hover {
  background-color: #f8f9fa;
  color: #495057;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

/* Content */
.restriction-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1rem 1rem;
}

.restriction-title {
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
}

.restriction-text {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

/* Systems */
.systems-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1.5rem;
}

.system-card {
  flex: 1;
  max-width: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  background: white;
}

.system-card-target {
  border-color: #10A0D0;
}

.system-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #adb5bd;
}

.system-logo {
  max-width: 100%;
  max-height: 40px;
  object-fit: contain;
}

.system-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
}

.arrow {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #adb5bd;
}

/* Sad face */
.sad-face {
  width: 72px;
  height: 72px;
  color: #adb5bd;
  margin-bottom: 1rem;
}

/* Actions */
.actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .systems-row {
    flex-direction: column;
  }

  .arrow {
    transform: rotate(90deg);
  }

  .system-card {
    max-width: 100%;
    width: 100%;
  }
}
</style>
